/**
 * Created by orion on 19.02.17.
 */
import { createApp } from '~/vue/createApp';
import simpleTextInputPopup from './simpleTextInputPopup.vue';
import OptionalRoleSetPopup from './role_popup/OptionalRoleSetPopup.vue';
import SelectFromActionsPopup from './SelectFromActionsPopup.vue';
import SimplePopup from './SimplePopup';

function simplePopupFactory() {
    return {
        show_popup, // +
        showPopupWithReason,
        simple_text_input_popup, // +
        optional_role_popup,
        mount_vue_popup, // +
        showSelectFromActionsPopup
    };

    /**
     * Mounts `popupComponent` on a container of its own, appended to the body.
     *
     * The popup used to be rendered as the child of a throwaway root, so that vue 2's
     * `attrs` / `props` / `listeners` render-function buckets could be filled separately.
     * Vue 3 has one bucket - a prop the component declares arrives as a prop, anything
     * else falls through to `$attrs`, which is what the two used to mean - so the popup is
     * now the root component itself and `attrs` and `props` are merged. `listeners` is
     * kept in the signature (no caller passes one) and converted to the `onEvent` form.
     */
    function mount_vue_popup(popupComponent, attrs = {}, listeners = {}, props = {}) {
        const fEl = document.activeElement;
        const divForPopup = document.createElement('div');
        document.body.appendChild(divForPopup);

        const rootProps = { ...attrs, ...props };
        for (const [event, handler] of Object.entries(listeners)) {
            rootProps['on' + event.charAt(0).toUpperCase() + event.slice(1)] = handler;
        }

        const app = createApp(popupComponent, rootProps);
        const mountedPopup = app.mount(divForPopup);

        return {
            // every caller uses the handle for `$destroy()` and nothing else. Unmounting
            // takes the container with it - vue 2's $destroy() left the rendered element
            // behind on the body, one per popup ever opened
            vm: {
                $destroy() {
                    app.unmount();
                    divForPopup.remove();
                }
            },
            popupComponent: mountedPopup,
            fEl
        };
    }

    function simple_text_input_popup(header, message, buttonYes, buttonNo, initial, maxLength, element = 'input', firstFocusToSave = false) { //element: input or textarea
        return new Promise((resolve, reject) => {
            const {
                vm,
                popupComponent,
                fEl
            } = mount_vue_popup(simpleTextInputPopup);
            popupComponent.show(header, message, buttonYes, buttonNo, initial, maxLength, element, firstFocusToSave)
                .then(text => {
                    resolve(text);
                }, c => {
                    reject();
                })
                .finally(() => {
                    fEl?.focus();
                    vm?.$destroy();
                });
        });
    }

    function optional_role_popup() {
        const {
            vm,
            popupComponent,
            fEl
        } = mount_vue_popup(OptionalRoleSetPopup);
        return popupComponent.show()
            .finally(() => {
                fEl?.focus();
                vm?.$destroy();
            });
    }

    function show_popup(header, message, button_yes, button_no = undefined, button_yes_class = 'btn-green', size = undefined) {
        const {
            vm,
            popupComponent,
            fEl
        } = mount_vue_popup(SimplePopup);
        return new Promise((resolve, reject) => {
            popupComponent.show(header, message, button_yes, button_no, button_yes_class, size)
                .then(y => {
                    resolve(true);
                }, c => {
                    reject();
                })
                .finally(() => {
                    fEl?.focus();
                    vm?.$destroy();
                });
        });
    }

    /**
     * show_popup(), but the rejection carries why the popup went away: 'cancel' for the "no"
     * button, 'close' for the X. show_popup() swallows that, and most callers pipe the
     * rejection straight into notifications.error(), so it stays swallowed there.
     */
    function showPopupWithReason(header, message, button_yes, button_no = undefined, button_yes_class = 'btn-green', size = undefined) {
        const {
            vm,
            popupComponent,
            fEl
        } = mount_vue_popup(SimplePopup);
        return new Promise((resolve, reject) => {
            popupComponent.show(header, message, button_yes, button_no, button_yes_class, size)
                .then(y => {
                    resolve(true);
                }, reason => {
                    reject(reason);
                })
                .finally(() => {
                    fEl?.focus();
                    vm?.$destroy();
                });
        });
    }

    function showSelectFromActionsPopup(actions, header) {
        let resolve, reject;

        const {
            vm,
            popupComponent
        } = mount_vue_popup(SelectFromActionsPopup);
        popupComponent.show(actions, header)
            .then(action => {
                resolve(action);
            }, reason => {
                reject(reason);
            })
            .finally(() => {
                if (vm) {
                    vm.$destroy();
                }
            });

        return new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
    }
}

const spfInstance = simplePopupFactory();
export { spfInstance as simplePopupFactory };
